import { StateCreator } from 'zustand'
import type { AppStore } from '../index'
import { logger } from '../../utils/storeLogger'
import { Bookmark, Category } from '../../types/bookmark'

type State = Partial<AppStore> & {
  bookmarks?: Bookmark[]
  categories?: Category[]
}

export const createAuthMiddleware = <T extends AppStore>(
  config: StateCreator<T>
): StateCreator<T> => (set, get, api) => {
  return config(
    (...args) => {
      const currentUser = (get() as AppStore).currentUser
      const [state] = args as [State]

      // 如果是设置用户或主题相关操作，直接通过
      if (
        'currentUser' in state ||
        'theme' in state
      ) {
        logger.info('System operation', currentUser?.id, 
          'currentUser' in state ? 'SET_USER' : 'SET_THEME', 
          { state }
        )
        return set(...args)
      }

      // 用户未登录，阻止数据操作
      if (!currentUser) {
        logger.warn('Unauthorized operation attempted', undefined, 'AUTH_CHECK', { state })
        console.warn('User not authenticated, operation blocked')
        return
      }

      // 过滤数据，只保留当前用户的数据
      if ('bookmarks' in state && state.bookmarks) {
        const originalLength = state.bookmarks.length
        state.bookmarks = state.bookmarks.filter((b: Bookmark) => b.userId === currentUser.id)
        if (originalLength !== state.bookmarks.length) {
          logger.warn('Filtered out unauthorized bookmarks', currentUser.id, 'FILTER_BOOKMARKS', {
            filtered: originalLength - state.bookmarks.length
          })
        }
      }
      if ('categories' in state && state.categories) {
        const originalLength = state.categories.length
        state.categories = state.categories.filter((c: Category) => c.userId === currentUser.id)
        if (originalLength !== state.categories.length) {
          logger.warn('Filtered out unauthorized categories', currentUser.id, 'FILTER_CATEGORIES', {
            filtered: originalLength - state.categories.length
          })
        }
      }

      logger.info('Operation processed', currentUser.id, 'STATE_UPDATE', { state })
      return set(...args)
    },
    get,
    api
  )
} 